import {createLiveTreeMemo, createGuidedViewMemo, champPlacementFrom} from '../../scanpack/boxChampGuided';
import {handoffState, HANDOFF_IDLE} from '../../scanpack/boxChampHandoff';
import {v2ModalSuppressesHandoffCard} from '../../scanpack/boxChampModalFlags';
import {initialBoxChampState} from '../../../reducers/scanpackScanningInitialState';

const liveTreeMemo = createLiveTreeMemo();
const guidedViewMemo = createGuidedViewMemo();

const NONE = [];

const scanningOf = state => state?.scanPackScanning || {};
export const boxChampOf = state => scanningOf(state).boxChamp || initialBoxChampState;
export const scanPackSettingsOf = state => state.user?.bothSettings?.data?.data?.scanpack_setting || {};
export const generalSettingsOf = state => state.user?.bothSettings?.data?.data?.general_setting || {};

export const isGuidedVerificationOn = state =>
  Boolean(scanPackSettingsOf(state).box_scan_verification);

export const getNotFulfilledItems = state => {
  const local = boxChampOf(state).notFulfilledItems;
  if (local != null) return local;
  const order = scanningOf(state).currentOrder;
  return [...(order?.not_fulfilled_items || []), ...(order?.held_items || [])];
};

export const isBoxChampEnabled = state => Boolean(generalSettingsOf(state).is_box_champ);

const ROUND_TEMPLATE = '_kitRoundChildTemplate';

const withFullDependsTotals = item => {
  const template = item && item[ROUND_TEMPLATE];
  if (!Array.isArray(template)) return item;
  const kits = Number(item.total_qty) || 0;
  const live = Array.isArray(item.child_items) ? item.child_items : [];
  const sameComponent = (a, b) =>
    a.kit_product_id != null ? a.kit_product_id === b.kit_product_id : a.sku === b.sku;
  return {
    ...item,
    qty_remaining: Math.max(kits - (Number(item.scanned_qty) || 0), 0),
    child_items: template.map(perRound => {
      const current = live.find(child => sameComponent(perRound, child)) || perRound;
      const scanned = Number(current.scanned_qty) || 0;
      const total = (Number(perRound.qty_remaining) || 0) * kits;
      return {...current, scanned_qty: scanned, qty_remaining: Math.max(total - scanned, 0)};
    })
  };
};

const hasDependsRounds = items => (items || []).some(item => Array.isArray(item?.[ROUND_TEMPLATE]));

let fullTotalsIn = null;
let fullTotalsOut = null;
const orderWithFullDependsTotals = order => {
  if (order === fullTotalsIn) return fullTotalsOut;
  fullTotalsIn = order;
  fullTotalsOut =
    hasDependsRounds(order.unscanned_items) || hasDependsRounds(order.scanned_items)
      ? {
          ...order,
          unscanned_items: (order.unscanned_items || []).map(withFullDependsTotals),
          scanned_items: (order.scanned_items || []).map(withFullDependsTotals)
        }
      : order;
  return fullTotalsOut;
};

let liveCardsIn = null;
let liveCardsOrder = null;
let liveCardsOut = null;
const withLiveCardItems = (view, order) => {
  if (!view || !hasDependsRounds(order.unscanned_items)) return view;
  if (view === liveCardsIn && order === liveCardsOrder) return liveCardsOut;
  const live = order.unscanned_items || [];
  const liveItem = item => live.find(candidate => candidate && candidate.order_item_id === item.order_item_id);
  liveCardsIn = view;
  liveCardsOrder = order;
  liveCardsOut = {
    ...view,
    activeItems: view.activeItems.map(liveItem).filter(Boolean),
    unboxedItems: view.unboxedItems.map(liveItem).filter(Boolean)
  };
  return liveCardsOut;
};

export const getGuidedView = state => {
  const order = scanningOf(state).currentOrder;
  if (!order || !champPlacementFrom(order)) return null;
  const local = boxChampOf(state).notFulfilledItems;
  const tree = liveTreeMemo(orderWithFullDependsTotals(order), local == null ? order.not_fulfilled_items : local);
  return withLiveCardItems(guidedViewMemo(tree, [order]), order);
};

export const getGuidedSealBaselineIds = (state, view = getGuidedView(state)) => {
  if (!view) return NONE;
  const orderId = scanningOf(state).currentOrder?.id;
  const stored = boxChampOf(state).sealBaseline;
  if (orderId != null && stored && stored.orderId === orderId) return stored.ids;
  return view.projection.boxes.filter(entry => entry.sealed).map(entry => entry.box.id);
};

export const getGuidedAutoSealedIds = (state, view = getGuidedView(state)) => {
  if (!view) return NONE;
  const baseline = getGuidedSealBaselineIds(state, view);
  const confirmed = boxChampOf(state).sealConfirmedBoxIds || NONE;
  return view.projection.boxes
    .filter(
      entry =>
        entry.sealed && !baseline.includes(entry.box.id) && !confirmed.includes(entry.box.id)
    )
    .map(entry => entry.box.id);
};

export const isGuidedModalOpen = state => v2ModalSuppressesHandoffCard(state);

export const getGuidedHandoff = (state, {ignoreModal = false} = {}) => {
  const view = getGuidedView(state);
  const boxChamp = boxChampOf(state);
  return handoffState({
    view,
    verificationOn: isGuidedVerificationOn(state),
    verifiedBoxIds: boxChamp.handoffVerifiedBoxIds,
    justConfirmedBoxId: boxChamp.justConfirmedBoxId,
    autoSealedBoxIds: getGuidedAutoSealedIds(state, view),
    sealConfirmedBoxIds: boxChamp.sealConfirmedBoxIds,
    modalOpen: ignoreModal ? false : isGuidedModalOpen(state)
  });
};

export const isGuidedOrderPacked = (state, view = getGuidedView(state)) =>
  Boolean(view && view.projection.allComplete && view.unboxedItems.length === 0);

export const isGuidedOrderAwaitingLastSeal = state => {
  const guided = boxChampOf(state);
  const order = scanningOf(state).currentOrder;
  return Boolean(
    guided.completionPending &&
      !guided.completionReleased &&
      order &&
      (order.unscanned_items || []).length === 0
  );
};

export const isGuidedPaused = state =>
  getGuidedHandoff(state, {ignoreModal: true}).kind !== HANDOFF_IDLE;

export const guidedHandoffKey = handoff =>
  !handoff || handoff.kind === HANDOFF_IDLE
    ? null
    : `${handoff.kind}:${handoff.sealBox?.id ?? ''}:${handoff.expectedBox?.id ?? ''}`;

export const isGuidedHandoffDismissed = (state, handoff = getGuidedHandoff(state)) => {
  const key = guidedHandoffKey(handoff);
  return key !== null && key === boxChampOf(state).handoffDismissedKey;
};

export const guidedHandoffOwnsFocus = state => {
  const handoff = getGuidedHandoff(state);
  return Boolean(
    handoff.kind !== HANDOFF_IDLE &&
      handoff.requiresBoxScan &&
      !isGuidedHandoffDismissed(state, handoff)
  );
};

export const getGuidedActiveBoxId = (state, view = getGuidedView(state)) =>
  view ? view.projection.activeBoxId : null;

export const getGuidedPendingConfirmationBoxId = state =>
  getGuidedHandoff(state, {ignoreModal: true}).sealBox?.id ?? null;

export const guidedLabelFor = (view, row) =>
  view.labels[`${row.order_item_id}:${row.kit_id ?? ''}`] ||
  (view.labelsByProduct || {})[row.product_id] ||
  null;

const barcodesOf = entry => (entry.barcodes || []).map(b => b && b.barcode).filter(Boolean);
export const getGuidedUnboxedBarcodes = view =>
  view
    ? view.unboxedItems.flatMap(item => [
        ...barcodesOf(item),
        ...(Array.isArray(item.child_items) ? item.child_items : []).filter(Boolean).flatMap(barcodesOf)
      ])
    : NONE;

export const getGuidedSetAsideItems = (view, handoff) => {
  if (!view || !handoff || !handoff.nextBox) return NONE;
  const entry = view.projection.boxes.find(e => e.box.id === handoff.nextBox.id);
  if (!entry) return NONE;
  return entry.rows
    .filter(r => r.filled < r.target)
    .map(r => {
      const label = guidedLabelFor(view, r.row);
      return {
        key: `${r.row.id}`,
        order_item_id: r.row.order_item_id,
        name: (label && (label.name || label.sku)) || 'Item',
        qty: r.target - r.filled
      };
    });
};

export const getGuidedSealedContents = (view, handoff) => {
  if (!view || !handoff || !handoff.sealBox) return NONE;
  const entry = view.projection.boxes.find(e => e.box.id === handoff.sealBox.id);
  if (!entry) return NONE;
  return entry.rows
    .filter(r => r.filled > 0)
    .map(r => {
      const label = guidedLabelFor(view, r.row) || {};
      return {
        key: `${r.row.id}`,
        name: label.name || label.sku || 'Item',
        sku: label.sku,
        image: label.image,
        qty: r.filled
      };
    });
};

export const getGuidedSealPosition = (view, handoff) => {
  const box = handoff && (handoff.sealBox || handoff.expectedBox);
  if (!view || !box) return null;
  const index = view.projection.boxes.findIndex(e => e.box.id === box.id);
  if (index === -1) return null;
  return `Box ${index + 1} of ${view.projection.totalBoxes}`;
};

export const createGuidedBundleSelector = () => {
  let last = null;
  return state => {
    const view = getGuidedView(state);
    const boxChamp = boxChampOf(state);
    const verificationOn = isGuidedVerificationOn(state);
    const modalOpen = isGuidedModalOpen(state);
    const orderId = scanningOf(state).currentOrder?.id ?? null;
    if (
      last &&
      last.view === view &&
      last.boxChamp === boxChamp &&
      last.verificationOn === verificationOn &&
      last.modalOpen === modalOpen &&
      last.orderId === orderId
    ) {
      return last.bundle;
    }

    const handoff = getGuidedHandoff(state);
    const enforcement = getGuidedHandoff(state, {ignoreModal: true});
    const dismissed = isGuidedHandoffDismissed(state, handoff);
    const bundle = {
      view,
      handoff,
      verificationOn,
      modalOpen,
      activeBoxId: view ? view.projection.activeBoxId : null,
      orderPacked: isGuidedOrderPacked(state, view),
      paused: enforcement.kind !== HANDOFF_IDLE,
      dismissed,
      ownsFocus: handoff.kind !== HANDOFF_IDLE && Boolean(handoff.requiresBoxScan) && !dismissed,
      pendingConfirmationBoxId: enforcement.sealBox?.id ?? null,
      setAsideItems: getGuidedSetAsideItems(view, handoff),
      sealedContents: getGuidedSealedContents(view, handoff),
      positionLabel: getGuidedSealPosition(view, handoff),
      unboxedBarcodes: getGuidedUnboxedBarcodes(view),
      labelFor: row => (view ? guidedLabelFor(view, row) : null)
    };
    last = {view, boxChamp, verificationOn, modalOpen, orderId, bundle};
    return bundle;
  };
};
